import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";

import SuggestedPostsCard from "./components/SuggestedPostsCard";
import useKeywordAnalysisDetail from "./hooks/useKeywordAnalysisDetail";
import { PageTitle } from "../../components/Common/PageTitle";
import { KeywordAnalysisCard } from "../../components/FacilityDetailsComponents/KeywordAnalysisCard";
import { useUpdateImpersonatedOrganizationContext } from "../../hooks/organizationImpersonation";
import { useMe } from "../../hooks/queries";
import { AccountType } from "../../models/AccountData";
import useLocation from "../FacilityDetailsPage/hooks/useLocation";

import "./styles.scss";

interface MatchParams {
  locationId: string;
  organizationId: string;
  keywordAnalysisId: string;
}

type ISearchKeywordAnalysisPageProps = RouteComponentProps<MatchParams>;

const SearchKeywordAnalysisPage: React.FunctionComponent<
  ISearchKeywordAnalysisPageProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityDetailsPage.keywordAnalysis.${key}`);

  const locationId = props.match.params.locationId;
  const organizationId = props.match.params.organizationId;
  const keywordAnalysisId = props.match.params.keywordAnalysisId;

  const { data: me } = useMe();
  const { data: locationData } = useLocation(locationId);

  const updateImpersonatedOrganizationContext =
    useUpdateImpersonatedOrganizationContext();

  useEffect(() => {
    if (me?.type === AccountType.ADMIN && organizationId) {
      updateImpersonatedOrganizationContext(organizationId);
    }
    return () => {
      updateImpersonatedOrganizationContext(null);
    };
  }, [me?.type, organizationId, updateImpersonatedOrganizationContext]);

  const { data: analysisData, isLoading: loading } = useKeywordAnalysisDetail(
    locationId,
    keywordAnalysisId,
    me?.type === AccountType.ADMIN ? organizationId : undefined
  );
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);

  const topKeyword = analysisData?.analysis_data?.top_keywords?.[0] || null;
  const otherKeywords =
    analysisData?.analysis_data?.top_keywords?.slice(1, 10) || [];

  if (loading) {
    return (
      <div className="right-content search-keyword-analysis-page">
        <div className="loading-container">{t("loading")}</div>
      </div>
    );
  }

  if (!analysisData || !topKeyword) {
    return (
      <div className="right-content search-keyword-analysis-page">
        <div className="no-data-container">{t("noDataAvailable")}</div>
      </div>
    );
  }

  return (
    <div className="right-content search-keyword-analysis-page">
      <div className="back-top-bar flex-grid">
        <NavLink
          to={
            organizationId
              ? `/location/${locationId}/organization/${organizationId}`
              : `/location/${locationId}`
          }
          className="back-link"
        >
          <i className="icons icon-back"></i>
          {t("back_to_facility_details")}
        </NavLink>
      </div>

      <div className="page-header">
        <PageTitle text={locationData?.name || ""} />
        <h2 className="page-subtitle">{t("keywordAnalysisTitle")}</h2>
      </div>

      <div className="analysis-content">
        <div className="top-keyword-section">
          <KeywordAnalysisCard
            keyword={topKeyword.keyword}
            searchCountTotal={topKeyword.search_count_total}
            combinedScore={topKeyword.combined_score}
            reviewMentionCount={topKeyword.review_mention_count}
            reason={topKeyword.reason}
            showDetailButton={false}
          />

          <div className="expand-detail-section">
            <button
              className="btn btn-blue"
              onClick={() => setIsDetailExpanded(!isDetailExpanded)}
            >
              {t("showDetail")}
              {isDetailExpanded ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
          </div>
        </div>

        {isDetailExpanded && (
          <div className="expanded-content">
            {otherKeywords.length > 0 && (
              <div className="other-keywords-section">
                <div className="keywords-grid">
                  {otherKeywords.map((keyword: any, index: number) => (
                    <div key={index} className="keyword-card">
                      <div className="keyword-header">
                        <div className="keyword-info">
                          <span className="keyword-rank">#{index + 2}</span>
                          <span className="keyword-name">
                            {keyword.keyword}
                          </span>
                        </div>
                        <div className="keyword-metrics-row">
                          <span>
                            {t("searchCountTotal")}:{" "}
                            {keyword.search_count_total.toLocaleString()}
                          </span>
                          <span>
                            {t("combinedScore")}:{" "}
                            {keyword.combined_score.toLocaleString()}
                          </span>
                          <span>
                            {t("reviewMentionCount")}:{" "}
                            {keyword.review_mention_count.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="keyword-reason">
                        <div className="reason-text">{keyword.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <SuggestedPostsCard
          locationId={locationId}
          locationData={locationData}
        />
      </div>
    </div>
  );
};

export default withRouter(SearchKeywordAnalysisPage);
